import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import React, { useCallback, useMemo, useState } from 'react'

import {
  compareByTimestamp,
  DEFAULT_SORT_DIRECTION,
  type SortDirection,
} from './NotificationListPanel'
import { NotificationsDropdownContent } from './NotificationsDropdownContent'
import { useNotificationPreferences } from './useNotificationPreferences'
import {
  type Notification,
  NotificationStatus,
  useNotifications,
} from './useNotifications'

export const DROPDOWN_CONTENT_STYLE = {
  width: '400px',
  overflow: 'hidden',
} as const

export interface NotificationsDropdownTriggerState {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  arePlatformNotificationsEnabled: boolean
  unreadCount: number
}

export type NotificationsDropdownContentProps = React.ComponentProps<
  typeof NotificationsDropdownContent
>

export const NotificationsDropdownBase: React.FC<{
  renderTrigger: (state: NotificationsDropdownTriggerState) => React.ReactNode
  renderContent: (
    contentProps: NotificationsDropdownContentProps
  ) => React.ReactNode
}> = ({ renderTrigger, renderContent }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    DEFAULT_SORT_DIRECTION
  )

  const { preferences } = useNotificationPreferences({
    enabled: true,
  })
  const arePlatformNotificationsEnabled = preferences.channels.inapp

  const { unreadCount } = useNotifications({ enabled: true })
  const {
    notifications,
    isLoading,
    error,
    updateNotificationStatus,
    markRead,
  } = useNotifications({
    enabled: isOpen,
    status: showAll ? undefined : NotificationStatus.UNREAD,
  })

  const handleMarkAsRead = useCallback(
    async (e: React.MouseEvent, notificationId: string) => {
      e.stopPropagation()
      try {
        await updateNotificationStatus(notificationId, NotificationStatus.READ)
      } catch (error) {
        console.error('Failed to mark notification as read:', error)
      }
    },
    [updateNotificationStatus]
  )

  const handleNotificationClick = useCallback(
    (
      e: React.MouseEvent,
      notification: Pick<Notification, 'id' | 'isUnread' | 'cta'>
    ) => {
      e.stopPropagation()

      if (notification.isUnread) {
        updateNotificationStatus(
          notification.id,
          NotificationStatus.READ
        ).catch((err) =>
          console.error('Failed to mark notification as read:', err)
        )
      }

      if (notification.cta?.url) {
        window.open(notification.cta.url, '_blank', 'noopener,noreferrer')
      }
    },
    [updateNotificationStatus]
  )

  const handleArchive = useCallback(
    async (e: React.MouseEvent, notificationId: string) => {
      e.stopPropagation()
      try {
        await updateNotificationStatus(
          notificationId,
          NotificationStatus.ARCHIVED
        )
      } catch (error) {
        console.error('Failed to archive notification:', error)
      }
    },
    [updateNotificationStatus]
  )

  const handleMarkAllAsRead = useCallback(async () => {
    const unreadIds = notifications.filter((n) => n.isUnread).map((n) => n.id)
    if (unreadIds.length === 0) return

    try {
      await markRead(unreadIds)
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error)
    }
  }, [notifications, markRead])

  const hasUnread = useMemo(
    () => notifications.some((n) => n.isUnread),
    [notifications]
  )

  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((first, second) =>
      compareByTimestamp(first, second, sortDirection)
    )
  }, [notifications, sortDirection])

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open)
    if (!open) {
      setShowAll(false)
      setSortDirection(DEFAULT_SORT_DIRECTION)
    }
  }, [])

  const contentProps: NotificationsDropdownContentProps = {
    hasUnread,
    isLoading,
    error,
    showAll,
    sortedNotifications,
    sortDirection,
    onArchive: handleArchive,
    onMarkAllAsRead: handleMarkAllAsRead,
    onMarkAsRead: handleMarkAsRead,
    onNotificationClick: handleNotificationClick,
    onShowAllChange: setShowAll,
    onSortDirectionChange: setSortDirection,
    onClose: () => setIsOpen(false),
  }

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={handleOpenChange}>
      {renderTrigger({
        isOpen,
        setIsOpen,
        arePlatformNotificationsEnabled,
        unreadCount,
      })}
      {renderContent(contentProps)}
    </DropdownMenu.Root>
  )
}
